import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import { 
  Building2, Plus, Edit, Trash2, ArrowLeft, RefreshCw, 
  Wrench, Shield, Sparkles, Truck, Zap, MessageSquare,
  AlertTriangle, CheckCircle, Clock, Settings, Users,
  ClipboardList, Thermometer, Droplets, BarChart3,
  Activity, FileText, Target, HardHat, Send, Store, QrCode,
  Package, Brush, UserCog
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Asset categories with icons
const ASSET_CATEGORIES = [
  { value: "hvac", label: "HVAC", icon: Thermometer },
  { value: "elevator", label: "Elevator", icon: Building2 },
  { value: "escalator", label: "Escalator", icon: Activity },
  { value: "dg_set", label: "DG Set", icon: Zap },
  { value: "fire_panel", label: "Fire Panel", icon: AlertTriangle },
  { value: "electrical_panel", label: "Electrical Panel", icon: Zap },
  { value: "cctv", label: "CCTV", icon: Shield },
  { value: "plumbing", label: "Plumbing", icon: Droplets },
  { value: "other", label: "Other", icon: Settings },
];

const PRIORITY_COLORS = {
  critical: "bg-red-500/20 text-red-400 border-red-500/30",
  high: "bg-orange-500/20 text-orange-400 border-orange-500/30",
  medium: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  low: "bg-blue-500/20 text-blue-400 border-blue-500/30",
};

const STATUS_COLORS = {
  open: "bg-red-500/20 text-red-400",
  assigned: "bg-yellow-500/20 text-yellow-400",
  in_progress: "bg-blue-500/20 text-blue-400",
  completed: "bg-green-500/20 text-green-400",
  closed: "bg-gray-500/20 text-gray-400",
  operational: "bg-green-500/20 text-green-400",
  under_maintenance: "bg-yellow-500/20 text-yellow-400",
  breakdown: "bg-red-500/20 text-red-400",
  pending: "bg-yellow-500/20 text-yellow-400",
  approved: "bg-green-500/20 text-green-400",
  active: "bg-green-500/20 text-green-400",
};

export default function MallOperationsPage() {
  const { mallId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [mall, setMall] = useState(null);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [dashboard, setDashboard] = useState(null);
  
  // Data states
  const [assets, setAssets] = useState([]);
  const [maintenanceJobs, setMaintenanceJobs] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [workPermits, setWorkPermits] = useState([]);
  const [gatePasses, setGatePasses] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [tenantRequests, setTenantRequests] = useState([]);
  const [tenantRequestsDash, setTenantRequestsDash] = useState(null);
  
  // Housekeeping & Inventory data
  const [cleaningSchedules, setCleaningSchedules] = useState([]);
  const [washroomScans, setWashroomScans] = useState([]);
  const [housekeepingDash, setHousekeepingDash] = useState(null);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [inventoryDash, setInventoryDash] = useState(null);
  const [facilityAlerts, setFacilityAlerts] = useState([]);
  const [mallStaff, setMallStaff] = useState([]);
  
  // Dialog states
  const [showAssetDialog, setShowAssetDialog] = useState(false);
  const [showMaintenanceDialog, setShowMaintenanceDialog] = useState(false);
  const [showIncidentDialog, setShowIncidentDialog] = useState(false);
  const [showPermitDialog, setShowPermitDialog] = useState(false);
  const [showGatePassDialog, setShowGatePassDialog] = useState(false);
  const [showVendorDialog, setShowVendorDialog] = useState(false);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [showInventoryDialog, setShowInventoryDialog] = useState(false);
  const [showStockDialog, setShowStockDialog] = useState(false);
  
  // Form states
  const [assetForm, setAssetForm] = useState({
    asset_name: "", asset_category: "hvac", serial_number: "",
    location_zone: "", floor: 0, manufacturer: "", model_number: "",
    criticality: "medium", commissioning_date: "", warranty_expiry: ""
  });
  
  const [maintenanceForm, setMaintenanceForm] = useState({
    title: "", description: "", maintenance_type: "breakdown",
    priority: "medium", asset_id: "", location_zone: "", floor: 0
  });
  
  const [incidentForm, setIncidentForm] = useState({
    title: "", description: "", incident_type: "other",
    severity: "medium", location_zone: "", floor: 0
  });
  
  const [permitForm, setPermitForm] = useState({
    permit_type: "general", contractor_name: "", contractor_company: "",
    contractor_phone: "", work_description: "", location_zone: "",
    floor: 0, start_date: "", end_date: "", workers_count: 1
  });
  
  const [gatePassForm, setGatePassForm] = useState({
    pass_type: "visitor", person_name: "", company: "",
    purpose: "", valid_from: "", valid_until: "", vehicle_number: ""
  });
  
  const [vendorForm, setVendorForm] = useState({
    vendor_name: "", vendor_type: "maintenance", contact_person: "",
    contact_phone: "", contact_email: "", services: []
  });

  // Housekeeping & Inventory forms
  const [scheduleForm, setScheduleForm] = useState({
    zone: "", floor: 0, area_type: "common_area", frequency: "daily",
    assigned_staff: "", start_time: "", notes: ""
  });
  
  const [inventoryForm, setInventoryForm] = useState({
    item_name: "", category: "cleaning_supplies", unit: "pcs",
    current_stock: 0, min_stock_level: 0, reorder_level: 0,
    location: "", supplier: "", unit_price: 0
  });
  
  const [stockForm, setStockForm] = useState({
    item_id: "", movement_type: "in", quantity: 0, reference: "", notes: ""
  });

  useEffect(() => {
    if (mallId && token) {
      fetchAllData();
    }
  }, [mallId, token]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [mallRes, dashRes, assetsRes, jobsRes, incRes, permitsRes, passesRes, vendorsRes, feedbackRes, tenantReqRes, tenantDashRes] = await Promise.all([
        axios.get(`${API}/api/malls/${mallId}`, { headers }),
        axios.get(`${API}/api/operations/dashboard/${mallId}`, { headers }),
        axios.get(`${API}/api/operations/assets/${mallId}`, { headers }),
        axios.get(`${API}/api/operations/maintenance/${mallId}`, { headers }),
        axios.get(`${API}/api/operations/incidents/${mallId}`, { headers }),
        axios.get(`${API}/api/operations/work-permits/${mallId}`, { headers }),
        axios.get(`${API}/api/operations/gate-passes/${mallId}`, { headers }),
        axios.get(`${API}/api/operations/vendors/${mallId}`, { headers }),
        axios.get(`${API}/api/operations/feedback/${mallId}`, { headers }),
        axios.get(`${API}/api/tenant-service/mall/${mallId}/requests`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/api/tenant-service/dashboard/${mallId}/tenant-requests`, { headers }).catch(() => ({ data: null })),
      ]);
      
      setMall(mallRes.data);
      setDashboard(dashRes.data);
      setAssets(assetsRes.data);
      setMaintenanceJobs(jobsRes.data);
      setIncidents(incRes.data);
      setWorkPermits(permitsRes.data);
      setGatePasses(passesRes.data);
      setVendors(vendorsRes.data);
      setFeedback(feedbackRes.data);
      setTenantRequests(tenantReqRes.data);
      setTenantRequestsDash(tenantDashRes.data);
      
      // Fetch housekeeping and inventory data
      try {
        const [schedulesRes, scansRes, hkDashRes, invRes, invDashRes, alertsRes, staffRes] = await Promise.all([
          axios.get(`${API}/api/facility/housekeeping/schedules/${mallId}`, { headers }),
          axios.get(`${API}/api/facility/housekeeping/washroom-scans/${mallId}`, { headers }),
          axios.get(`${API}/api/facility/housekeeping/dashboard/${mallId}`, { headers }),
          axios.get(`${API}/api/facility/inventory/${mallId}`, { headers }),
          axios.get(`${API}/api/facility/inventory/${mallId}/dashboard`, { headers }),
          axios.get(`${API}/api/facility/alerts/${mallId}?status=open`, { headers }),
          axios.get(`${API}/api/users/staff/${mallId}`, { headers }).catch(() => ({ data: [] })),
        ]);
        
        setCleaningSchedules(schedulesRes.data);
        setWashroomScans(scansRes.data);
        setHousekeepingDash(hkDashRes.data);
        setInventoryItems(invRes.data);
        setInventoryDash(invDashRes.data);
        setFacilityAlerts(alertsRes.data);
        setMallStaff(staffRes.data);
      } catch (e) {
        console.log("Some facility data not available:", e);
      }
      
    } catch (err) {
      console.error("Failed to fetch operations data:", err);
      toast.error("Failed to load operations data");
    } finally {
      setLoading(false);
    }
  };

  // Create handlers
  const handleCreateAsset = async () => {
    try {
      await axios.post(`${API}/api/operations/assets`, 
        { ...assetForm, mall_id: mallId },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success("Asset created successfully");
      setShowAssetDialog(false);
      setAssetForm({ asset_name: "", asset_category: "hvac", serial_number: "", location_zone: "", floor: 0, manufacturer: "", model_number: "", criticality: "medium", commissioning_date: "", warranty_expiry: "" });
      fetchAllData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create asset");
    }
  };

  const handleCreateMaintenanceJob = async () => {
    try {
      await axios.post(`${API}/api/operations/maintenance`,
        { ...maintenanceForm, mall_id: mallId },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success("Maintenance job created");
      setShowMaintenanceDialog(false);
      setMaintenanceForm({ title: "", description: "", maintenance_type: "breakdown", priority: "medium", asset_id: "", location_zone: "", floor: 0 });
      fetchAllData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create job");
    }
  };

  const handleCreateIncident = async () => {
    try {
      await axios.post(`${API}/api/operations/incidents`,
        { ...incidentForm, mall_id: mallId },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success("Incident reported");
      setShowIncidentDialog(false);
      setIncidentForm({ title: "", description: "", incident_type: "other", severity: "medium", location_zone: "", floor: 0 });
      fetchAllData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to report incident");
    }
  };

  const handleCreatePermit = async () => {
    try {
      await axios.post(`${API}/api/operations/work-permits`,
        { ...permitForm, mall_id: mallId },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success("Work permit created");
      setShowPermitDialog(false);
      setPermitForm({ permit_type: "general", contractor_name: "", contractor_company: "", contractor_phone: "", work_description: "", location_zone: "", floor: 0, start_date: "", end_date: "", workers_count: 1 });
      fetchAllData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create permit");
    }
  };

  const handleCreateGatePass = async () => {
    try {
      await axios.post(`${API}/api/operations/gate-passes`,
        { ...gatePassForm, mall_id: mallId },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success("Gate pass created");
      setShowGatePassDialog(false);
      setGatePassForm({ pass_type: "visitor", person_name: "", company: "", purpose: "", valid_from: "", valid_until: "", vehicle_number: "" });
      fetchAllData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create gate pass");
    }
  };

  const handleCreateVendor = async () => {
    try {
      await axios.post(`${API}/api/operations/vendors`,
        { ...vendorForm, mall_id: mallId },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success("Vendor added");
      setShowVendorDialog(false);
      setVendorForm({ vendor_name: "", vendor_type: "maintenance", contact_person: "", contact_phone: "", contact_email: "", services: [] });
      fetchAllData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add vendor");
    }
  };

  const handleApprovePermit = async (permitId) => {
    try {
      await axios.put(`${API}/api/operations/work-permits/${permitId}/approve`, {},
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success("Work permit approved");
      fetchAllData();
    } catch (err) {
      toast.error("Failed to approve permit");
    }
  };

  const handleApproveGatePass = async (passId) => {
    try {
      await axios.put(`${API}/api/operations/gate-passes/${passId}/approve`, {},
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success("Gate pass approved");
      fetchAllData();
    } catch (err) {
      toast.error("Failed to approve gate pass");
    }
  };

  // Housekeeping handlers
  const handleCreateSchedule = async () => {
    try {
      await axios.post(`${API}/api/facility/housekeeping/schedules`,
        { ...scheduleForm, mall_id: mallId },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success("Cleaning schedule created");
      setShowScheduleDialog(false);
      setScheduleForm({ zone: "", floor: 0, area_type: "common_area", frequency: "daily", assigned_staff: "", start_time: "", notes: "" });
      fetchAllData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create schedule");
    }
  };

  // Inventory handlers
  const handleCreateInventoryItem = async () => {
    try {
      await axios.post(`${API}/api/facility/inventory`,
        { ...inventoryForm, mall_id: mallId },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success("Inventory item created");
      setShowInventoryDialog(false);
      setInventoryForm({ item_name: "", category: "cleaning_supplies", unit: "pcs", current_stock: 0, min_stock_level: 0, reorder_level: 0, location: "", supplier: "", unit_price: 0 });
      fetchAllData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create item");
    }
  };

  const handleStockMovement = async () => {
    try {
      await axios.post(`${API}/api/facility/inventory/movement`,
        stockForm,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success("Stock movement recorded");
      setShowStockDialog(false);
      setStockForm({ item_id: "", movement_type: "in", quantity: 0, reference: "", notes: "" });
      fetchAllData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to record movement");
    }
  };

  const handleResolveAlert = async (alertId) => {
    const resolution = prompt("Enter resolution notes:");
    if (resolution !== null) {
      try {
        await axios.put(`${API}/api/facility/alerts/${alertId}/resolve?resolution=${encodeURIComponent(resolution)}`, {},
          { headers: { Authorization: `Bearer ${token}` }}
        );
        toast.success("Alert resolved");
        fetchAllData();
      } catch (err) {
        toast.error("Failed to resolve alert");
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
          <p className="text-muted-foreground">Loading operations data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="mall-operations-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/malls")} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{mall?.name} - Operations</h1>
            <p className="text-muted-foreground">{mall?.city}, {mall?.state}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchAllData} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button variant="outline" onClick={() => navigate(`/mall/${mallId}/qr-codes`)} data-testid="qr-btn">
            <QrCode className="w-4 h-4 mr-2" />
            QR Codes
          </Button>
          <Button variant="outline" onClick={() => navigate(`/mall/${mallId}/manage`)} data-testid="manage-btn">
            <Settings className="w-4 h-4 mr-2" />
            Manage Units
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex flex-wrap w-full max-w-6xl gap-1">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">
            <BarChart3 className="w-4 h-4 mr-1" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="tenant-requests" data-testid="tab-tenant-requests">
            <Store className="w-4 h-4 mr-1" />
            Tenant
          </TabsTrigger>
          <TabsTrigger value="assets" data-testid="tab-assets">
            <Settings className="w-4 h-4 mr-1" />
            Assets
          </TabsTrigger>
          <TabsTrigger value="maintenance" data-testid="tab-maintenance">
            <Wrench className="w-4 h-4 mr-1" />
            Maintenance
          </TabsTrigger>
          <TabsTrigger value="housekeeping" data-testid="tab-housekeeping">
            <Brush className="w-4 h-4 mr-1" />
            Housekeeping
          </TabsTrigger>
          <TabsTrigger value="inventory" data-testid="tab-inventory">
            <Package className="w-4 h-4 mr-1" />
            Inventory
          </TabsTrigger>
          <TabsTrigger value="security" data-testid="tab-security">
            <Shield className="w-4 h-4 mr-1" />
            Security
          </TabsTrigger>
          <TabsTrigger value="permits" data-testid="tab-permits">
            <HardHat className="w-4 h-4 mr-1" />
            Permits
          </TabsTrigger>
          <TabsTrigger value="vendors" data-testid="tab-vendors">
            <Truck className="w-4 h-4 mr-1" />
            Vendors
          </TabsTrigger>
          <TabsTrigger value="staff" data-testid="tab-staff">
            <UserCog className="w-4 h-4 mr-1" />
            Staff
          </TabsTrigger>
          <TabsTrigger value="feedback" data-testid="tab-feedback">
            <MessageSquare className="w-4 h-4 mr-1" />
            Feedback
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          {dashboard && (
            <>
              {/* KPI Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="border-l-4 border-l-red-500">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-muted-foreground uppercase">Open Incidents</p>
                        <p className="text-2xl font-bold">{dashboard.open_incidents}</p>
                        <p className="text-xs text-red-400">{dashboard.critical_incidents} critical</p>
                      </div>
                      <AlertTriangle className="w-8 h-8 text-red-500/50" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-orange-500">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-muted-foreground uppercase">Open Jobs</p>
                        <p className="text-2xl font-bold">{dashboard.open_maintenance_jobs}</p>
                        <p className="text-xs text-orange-400">{dashboard.overdue_jobs} overdue</p>
                      </div>
                      <Wrench className="w-8 h-8 text-orange-500/50" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-green-500">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-muted-foreground uppercase">Total Assets</p>
                        <p className="text-2xl font-bold">{dashboard.total_assets}</p>
                        <p className="text-xs text-green-400">{dashboard.assets_breakdown} down</p>
                      </div>
                      <Settings className="w-8 h-8 text-green-500/50" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-blue-500">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-muted-foreground uppercase">SLA Compliance</p>
                        <p className="text-2xl font-bold">{dashboard.sla_compliance_percent}%</p>
                        <Progress value={dashboard.sla_compliance_percent} className="h-1 mt-2" />
                      </div>
                      <Target className="w-8 h-8 text-blue-500/50" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Second Row KPIs */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-muted-foreground uppercase">Energy Today</p>
                        <p className="text-2xl font-bold">{dashboard.today_energy_kwh?.toLocaleString()}</p>
                        <p className="text-xs text-muted-foreground">kWh</p>
                      </div>
                      <Zap className="w-8 h-8 text-yellow-500/50" />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-muted-foreground uppercase">Active Passes</p>
                        <p className="text-2xl font-bold">{dashboard.active_gate_passes}</p>
                        <p className="text-xs text-muted-foreground">gate passes</p>
                      </div>
                      <Users className="w-8 h-8 text-purple-500/50" />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-muted-foreground uppercase">Pending Permits</p>
                        <p className="text-2xl font-bold">{dashboard.pending_work_permits}</p>
                        <p className="text-xs text-muted-foreground">awaiting approval</p>
                      </div>
                      <FileText className="w-8 h-8 text-cyan-500/50" />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-muted-foreground uppercase">Customer Rating</p>
                        <p className="text-2xl font-bold">{dashboard.avg_customer_rating}/5</p>
                        <p className="text-xs text-red-400">{dashboard.negative_feedback_count} negative</p>
                      </div>
                      <MessageSquare className="w-8 h-8 text-pink-500/50" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Recent Activity */}
              <div className="grid md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Recent Incidents</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {incidents.slice(0, 5).length > 0 ? (
                      <div className="space-y-3">
                        {incidents.slice(0, 5).map(inc => (
                          <div key={inc.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                            <div className="flex items-center gap-3">
                              <Badge className={PRIORITY_COLORS[inc.severity]}>{inc.severity}</Badge>
                              <div>
                                <p className="font-medium text-sm">{inc.title}</p>
                                <p className="text-xs text-muted-foreground">{inc.incident_type}</p>
                              </div>
                            </div>
                            <Badge className={STATUS_COLORS[inc.status]}>{inc.status}</Badge>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-center py-6">No recent incidents</p>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Recent Maintenance</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {maintenanceJobs.slice(0, 5).length > 0 ? (
                      <div className="space-y-3">
                        {maintenanceJobs.slice(0, 5).map(job => (
                          <div key={job.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                            <div className="flex items-center gap-3">
                              <Badge className={PRIORITY_COLORS[job.priority]}>{job.priority}</Badge>
                              <div>
                                <p className="font-medium text-sm">{job.title}</p>
                                <p className="text-xs text-muted-foreground">{job.job_number}</p>
                              </div>
                            </div>
                            <Badge className={STATUS_COLORS[job.status]}>{job.status}</Badge>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-center py-6">No maintenance jobs</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </TabsContent>

        {/* Tenant Requests Tab */}
        <TabsContent value="tenant-requests" className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold">Tenant Service Requests ({tenantRequests.length})</h3>
              <p className="text-sm text-muted-foreground">Requests raised by tenants that need attention</p>
            </div>
          </div>
          
          {/* Tenant Request Stats */}
          {tenantRequestsDash && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="border-l-4 border-l-red-500">
                <CardContent className="p-4">
                  <p className="text-xs text-muted-foreground uppercase">Open Requests</p>
                  <p className="text-2xl font-bold">{tenantRequestsDash.open_requests}</p>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-orange-500">
                <CardContent className="p-4">
                  <p className="text-xs text-muted-foreground uppercase">Urgent</p>
                  <p className="text-2xl font-bold">{tenantRequestsDash.urgent_requests}</p>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-blue-500">
                <CardContent className="p-4">
                  <p className="text-xs text-muted-foreground uppercase">Today</p>
                  <p className="text-2xl font-bold">{tenantRequestsDash.today_requests}</p>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-green-500">
                <CardContent className="p-4">
                  <p className="text-xs text-muted-foreground uppercase">Avg Resolution</p>
                  <p className="text-2xl font-bold">{tenantRequestsDash.avg_resolution_hours}h</p>
                </CardContent>
              </Card>
            </div>
          )}

          <div className="grid gap-4">
            {tenantRequests.filter(r => r.status !== 'resolved' && r.status !== 'closed').map(req => (
              <Card key={req.id} data-testid={`tenant-request-${req.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className={`p-3 rounded-lg ${req.priority === 'urgent' ? 'bg-red-500/20' : 'bg-muted'}`}>
                        <Store className={`w-6 h-6 ${req.priority === 'urgent' ? 'text-red-500' : 'text-primary'}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-medium">{req.title}</p>
                          <Badge className={PRIORITY_COLORS[req.priority]}>{req.priority}</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">{req.ticket_number} | {req.category}</p>
                        <p className="text-sm mt-1">{req.description}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                          <span>Tenant: {req.tenant_name || 'Unknown'}</span>
                          <span>Unit: {req.unit_number || 'N/A'}</span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right space-y-2">
                      <Badge className={STATUS_COLORS[req.status]}>{req.status}</Badge>
                      <p className="text-xs text-muted-foreground">{new Date(req.created_at).toLocaleDateString()}</p>
                      <div className="flex gap-1">
                        {req.status === 'open' && (
                          <Button size="sm" variant="outline" onClick={async () => {
                            try {
                              await axios.put(`${API}/api/tenant-service/mall/requests/${req.id}/acknowledge`, {}, { headers: { Authorization: `Bearer ${token}` }});
                              toast.success("Request acknowledged");
                              fetchAllData();
                            } catch (e) { toast.error("Failed"); }
                          }}>Acknowledge</Button>
                        )}
                        {(req.status === 'acknowledged' || req.status === 'assigned') && (
                          <Button size="sm" onClick={async () => {
                            const resolution = prompt("Enter resolution notes:");
                            if (resolution) {
                              try {
                                await axios.put(`${API}/api/tenant-service/mall/requests/${req.id}/resolve?resolution=${encodeURIComponent(resolution)}`, {}, { headers: { Authorization: `Bearer ${token}` }});
                                toast.success("Request resolved");
                                fetchAllData();
                              } catch (e) { toast.error("Failed"); }
                            }
                          }}>Resolve</Button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {tenantRequests.filter(r => r.status !== 'resolved' && r.status !== 'closed').length === 0 && (
            <Card className="p-12 text-center">
              <Store className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground">No open tenant requests</p>
            </Card>
          )}
        </TabsContent>

        {/* Assets Tab */}
        <TabsContent value="assets" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Asset Registry ({assets.length})</h3>
            <Button onClick={() => setShowAssetDialog(true)} data-testid="add-asset-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Asset
            </Button>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {assets.map(asset => (
              <Card key={asset.id} data-testid={`asset-card-${asset.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="font-medium">{asset.asset_name}</p>
                      <p className="text-xs text-muted-foreground">{asset.asset_code}</p>
                    </div>
                    <Badge className={STATUS_COLORS[asset.status]}>{asset.status}</Badge>
                  </div>
                  <div className="space-y-1 text-sm">
                    <p><span className="text-muted-foreground">Category:</span> {asset.asset_category}</p>
                    <p><span className="text-muted-foreground">Location:</span> Floor {asset.floor}, {asset.location_zone || 'N/A'}</p>
                    <p><span className="text-muted-foreground">Criticality:</span> 
                      <Badge variant="outline" className="ml-2">{asset.criticality}</Badge>
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          {assets.length === 0 && (
            <Card className="p-12 text-center">
              <Settings className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground">No assets registered yet</p>
              <Button className="mt-4" onClick={() => setShowAssetDialog(true)}>Add First Asset</Button>
            </Card>
          )}
        </TabsContent>

        {/* Maintenance Tab */}
        <TabsContent value="maintenance" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Maintenance Jobs ({maintenanceJobs.length})</h3>
            <Button onClick={() => setShowMaintenanceDialog(true)} data-testid="add-maintenance-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Job
            </Button>
          </div>
          
          <div className="grid gap-4">
            {maintenanceJobs.map(job => (
              <Card key={job.id} data-testid={`maintenance-card-${job.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-muted rounded-lg">
                        <Wrench className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-medium">{job.title}</p>
                          <Badge className={PRIORITY_COLORS[job.priority]}>{job.priority}</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">{job.job_number} | {job.maintenance_type}</p>
                        <p className="text-sm mt-1">{job.description}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge className={STATUS_COLORS[job.status]}>{job.status}</Badge>
                      {job.sla_breached && <Badge variant="destructive" className="ml-2">SLA Breached</Badge>}
                      <p className="text-xs text-muted-foreground mt-2">
                        {new Date(job.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          {maintenanceJobs.length === 0 && (
            <Card className="p-12 text-center">
              <Wrench className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground">No maintenance jobs</p>
              <Button className="mt-4" onClick={() => setShowMaintenanceDialog(true)}>Create First Job</Button>
            </Card>
          )}
        </TabsContent>

        {/* Security Tab (Incidents + Gate Passes) */}
        <TabsContent value="security" className="space-y-6">
          {/* Incidents Section */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Incidents ({incidents.length})</h3>
              <Button onClick={() => setShowIncidentDialog(true)} data-testid="add-incident-btn">
                <Plus className="w-4 h-4 mr-2" />
                Report Incident
              </Button>
            </div>
            
            <div className="grid gap-4">
              {incidents.slice(0, 5).map(inc => (
                <Card key={inc.id} data-testid={`incident-card-${inc.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`p-3 rounded-lg ${inc.severity === 'critical' ? 'bg-red-500/20' : 'bg-muted'}`}>
                          <AlertTriangle className={`w-6 h-6 ${inc.severity === 'critical' ? 'text-red-500' : 'text-muted-foreground'}`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-medium">{inc.title}</p>
                            <Badge className={PRIORITY_COLORS[inc.severity]}>{inc.severity}</Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">{inc.incident_number} | {inc.incident_type}</p>
                          <p className="text-sm mt-1">{inc.description}</p>
                        </div>
                      </div>
                      <Badge className={STATUS_COLORS[inc.status]}>{inc.status}</Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Gate Passes Section */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Gate Passes ({gatePasses.length})</h3>
              <Button onClick={() => setShowGatePassDialog(true)} data-testid="add-gatepass-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Gate Pass
              </Button>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4">
              {gatePasses.slice(0, 6).map(pass => (
                <Card key={pass.id} data-testid={`gatepass-card-${pass.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-medium">{pass.person_name}</p>
                        <p className="text-xs text-muted-foreground">{pass.pass_number}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={STATUS_COLORS[pass.status]}>{pass.status}</Badge>
                        {pass.status === 'pending' && (
                          <Button size="sm" variant="outline" onClick={() => handleApproveGatePass(pass.id)}>
                            Approve
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="text-sm space-y-1">
                      <p><span className="text-muted-foreground">Type:</span> {pass.pass_type}</p>
                      <p><span className="text-muted-foreground">Purpose:</span> {pass.purpose}</p>
                      <p><span className="text-muted-foreground">Valid:</span> {pass.valid_from} to {pass.valid_until}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* Work Permits Tab */}
        <TabsContent value="permits" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Work Permits ({workPermits.length})</h3>
            <Button onClick={() => setShowPermitDialog(true)} data-testid="add-permit-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Permit
            </Button>
          </div>
          
          <div className="grid gap-4">
            {workPermits.map(permit => (
              <Card key={permit.id} data-testid={`permit-card-${permit.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-muted rounded-lg">
                        <HardHat className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-medium">{permit.contractor_company}</p>
                          <Badge variant="outline">{permit.permit_type}</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">{permit.permit_number}</p>
                        <p className="text-sm mt-1">{permit.work_description}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {permit.start_date} - {permit.end_date} | {permit.workers_count} workers
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={STATUS_COLORS[permit.status]}>{permit.status}</Badge>
                      {permit.status === 'pending' && (
                        <Button size="sm" onClick={() => handleApprovePermit(permit.id)}>
                          Approve
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          {workPermits.length === 0 && (
            <Card className="p-12 text-center">
              <HardHat className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground">No work permits</p>
              <Button className="mt-4" onClick={() => setShowPermitDialog(true)}>Create First Permit</Button>
            </Card>
          )}
        </TabsContent>

        {/* Vendors Tab */}
        <TabsContent value="vendors" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Vendors ({vendors.length})</h3>
            <Button onClick={() => setShowVendorDialog(true)} data-testid="add-vendor-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Vendor
            </Button>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {vendors.map(vendor => (
              <Card key={vendor.id} data-testid={`vendor-card-${vendor.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="font-medium">{vendor.vendor_name}</p>
                      <p className="text-xs text-muted-foreground">{vendor.vendor_code}</p>
                    </div>
                    <Badge className={STATUS_COLORS[vendor.status]}>{vendor.status}</Badge>
                  </div>
                  <div className="space-y-1 text-sm">
                    <p><span className="text-muted-foreground">Type:</span> {vendor.vendor_type}</p>
                    <p><span className="text-muted-foreground">Contact:</span> {vendor.contact_person}</p>
                    <p><span className="text-muted-foreground">Phone:</span> {vendor.contact_phone}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          {vendors.length === 0 && (
            <Card className="p-12 text-center">
              <Truck className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground">No vendors registered</p>
              <Button className="mt-4" onClick={() => setShowVendorDialog(true)}>Add First Vendor</Button>
            </Card>
          )}
        </TabsContent>

        {/* Feedback Tab */}
        <TabsContent value="feedback" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Customer Feedback ({feedback.length})</h3>
          </div>
          
          <div className="grid gap-4">
            {feedback.map(fb => (
              <Card key={fb.id} data-testid={`feedback-card-${fb.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`p-3 rounded-lg ${fb.sentiment === 'negative' ? 'bg-red-500/20' : fb.sentiment === 'positive' ? 'bg-green-500/20' : 'bg-muted'}`}>
                        <MessageSquare className={`w-6 h-6 ${fb.sentiment === 'negative' ? 'text-red-500' : fb.sentiment === 'positive' ? 'text-green-500' : 'text-muted-foreground'}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline">{fb.category}</Badge>
                          <span className="text-yellow-500">
                            {"★".repeat(fb.rating)}{"☆".repeat(5 - fb.rating)}
                          </span>
                        </div>
                        <p className="text-sm">{fb.comment || "No comment provided"}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {fb.zone ? `Zone: ${fb.zone}` : ''} {fb.floor !== undefined ? `| Floor ${fb.floor}` : ''}
                        </p>
                      </div>
                    </div>
                    <Badge className={fb.sentiment === 'negative' ? 'bg-red-500/20 text-red-400' : fb.sentiment === 'positive' ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20'}>
                      {fb.sentiment}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          {feedback.length === 0 && (
            <Card className="p-12 text-center">
              <MessageSquare className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground">No feedback yet</p>
            </Card>
          )}
        </TabsContent>

        {/* Housekeeping Tab */}
        <TabsContent value="housekeeping" className="space-y-6">
          {/* Housekeeping Stats */}
          {housekeepingDash && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="border-l-4 border-l-cyan-500">
                <CardContent className="p-4">
                  <p className="text-xs text-muted-foreground uppercase">Schedules</p>
                  <p className="text-2xl font-bold">{housekeepingDash.total_schedules}</p>
                  <p className="text-xs text-cyan-400">{housekeepingDash.completion_rate}% complete today</p>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-green-500">
                <CardContent className="p-4">
                  <p className="text-xs text-muted-foreground uppercase">Completed Today</p>
                  <p className="text-2xl font-bold">{housekeepingDash.completed_today}</p>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-yellow-500">
                <CardContent className="p-4">
                  <p className="text-xs text-muted-foreground uppercase">Washroom Scans</p>
                  <p className="text-2xl font-bold">{housekeepingDash.washroom_scans_today}</p>
                  <p className="text-xs text-yellow-400">Avg rating: {housekeepingDash.avg_cleanliness_rating}/5</p>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-red-500">
                <CardContent className="p-4">
                  <p className="text-xs text-muted-foreground uppercase">Open Alerts</p>
                  <p className="text-2xl font-bold">{housekeepingDash.open_consumable_alerts}</p>
                  <p className="text-xs text-red-400">Consumable issues</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Cleaning Schedules */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Cleaning Schedules ({cleaningSchedules.length})</h3>
              <Button onClick={() => setShowScheduleDialog(true)} data-testid="add-schedule-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Schedule
              </Button>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {cleaningSchedules.map(schedule => (
                <Card key={schedule.id} data-testid={`schedule-card-${schedule.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Brush className="w-5 h-5 text-cyan-500" />
                        <span className="font-medium capitalize">{schedule.area_type.replace('_', ' ')}</span>
                      </div>
                      <Badge variant="outline">{schedule.frequency}</Badge>
                    </div>
                    <div className="text-sm space-y-1">
                      <p><span className="text-muted-foreground">Zone:</span> {schedule.zone}</p>
                      <p><span className="text-muted-foreground">Floor:</span> {schedule.floor}</p>
                      {schedule.start_time && <p><span className="text-muted-foreground">Time:</span> {schedule.start_time}</p>}
                      {schedule.assigned_staff && <p><span className="text-muted-foreground">Assigned:</span> {schedule.assigned_staff}</p>}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            
            {cleaningSchedules.length === 0 && (
              <Card className="p-8 text-center">
                <Brush className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground">No cleaning schedules yet</p>
                <Button className="mt-4" onClick={() => setShowScheduleDialog(true)}>Create First Schedule</Button>
              </Card>
            )}
          </div>

          {/* Recent Washroom Scans */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Recent Washroom Scans</h3>
            <div className="grid gap-4">
              {washroomScans.slice(0, 5).map(scan => (
                <Card key={scan.id}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="p-2 bg-cyan-500/20 rounded-lg">
                          <Droplets className="w-5 h-5 text-cyan-500" />
                        </div>
                        <div>
                          <p className="font-medium capitalize">{scan.washroom_type} Washroom</p>
                          <p className="text-sm text-muted-foreground">Floor {scan.floor} • {scan.zone}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="text-yellow-500">
                          {"★".repeat(scan.cleanliness_rating)}{"☆".repeat(5 - scan.cleanliness_rating)}
                        </span>
                        <p className="text-xs text-muted-foreground">{new Date(scan.scanned_at).toLocaleString()}</p>
                      </div>
                    </div>
                    {scan.issues && scan.issues.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {scan.issues.map((issue, i) => (
                          <Badge key={i} variant="outline" className="text-red-400">{issue}</Badge>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
              {washroomScans.length === 0 && (
                <Card className="p-6 text-center">
                  <p className="text-muted-foreground">No washroom scans recorded today</p>
                </Card>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Inventory Tab */}
        <TabsContent value="inventory" className="space-y-6">
          {/* Inventory Stats */}
          {inventoryDash && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="border-l-4 border-l-blue-500">
                <CardContent className="p-4">
                  <p className="text-xs text-muted-foreground uppercase">Total Items</p>
                  <p className="text-2xl font-bold">{inventoryDash.total_items}</p>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-yellow-500">
                <CardContent className="p-4">
                  <p className="text-xs text-muted-foreground uppercase">Low Stock</p>
                  <p className="text-2xl font-bold">{inventoryDash.low_stock_count}</p>
                  <p className="text-xs text-yellow-400">Need reorder</p>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-red-500">
                <CardContent className="p-4">
                  <p className="text-xs text-muted-foreground uppercase">Critical</p>
                  <p className="text-2xl font-bold">{inventoryDash.critical_stock_count}</p>
                  <p className="text-xs text-red-400">Below minimum</p>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-green-500">
                <CardContent className="p-4">
                  <p className="text-xs text-muted-foreground uppercase">Total Value</p>
                  <p className="text-2xl font-bold">₹{inventoryDash.total_inventory_value?.toLocaleString()}</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Facility Alerts */}
          {facilityAlerts.length > 0 && (
            <Card className="border-orange-500/50 border">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-orange-500" />
                  Active Alerts ({facilityAlerts.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {facilityAlerts.slice(0, 5).map(alert => (
                    <div key={alert.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                      <div>
                        <p className="text-sm font-medium">{alert.message}</p>
                        <p className="text-xs text-muted-foreground">{alert.alert_type} • {new Date(alert.created_at).toLocaleString()}</p>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => handleResolveAlert(alert.id)}>
                        Resolve
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Inventory Items */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Inventory Items ({inventoryItems.length})</h3>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowStockDialog(true)} data-testid="stock-movement-btn">
                  <Activity className="w-4 h-4 mr-2" />
                  Stock Movement
                </Button>
                <Button onClick={() => setShowInventoryDialog(true)} data-testid="add-inventory-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Item
                </Button>
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {inventoryItems.map(item => (
                <Card key={item.id} data-testid={`inventory-card-${item.id}`} className={item.current_stock <= item.min_stock_level ? 'border-red-500/50' : item.current_stock <= item.reorder_level ? 'border-yellow-500/50' : ''}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-medium">{item.item_name}</p>
                        <p className="text-xs text-muted-foreground">{item.item_code}</p>
                      </div>
                      <Badge variant="outline" className="capitalize">{item.category.replace('_', ' ')}</Badge>
                    </div>
                    <div className="text-sm space-y-1">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Stock:</span>
                        <span className={item.current_stock <= item.min_stock_level ? 'text-red-500 font-bold' : item.current_stock <= item.reorder_level ? 'text-yellow-500' : ''}>
                          {item.current_stock} {item.unit}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Reorder Level:</span>
                        <span>{item.reorder_level} {item.unit}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Unit Price:</span>
                        <span>₹{item.unit_price}</span>
                      </div>
                      {item.location && <p><span className="text-muted-foreground">Location:</span> {item.location}</p>}
                    </div>
                    {item.current_stock <= item.min_stock_level && (
                      <Badge className="mt-2 bg-red-500/20 text-red-400">Critical - Below Min</Badge>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
            
            {inventoryItems.length === 0 && (
              <Card className="p-8 text-center">
                <Package className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground">No inventory items yet</p>
                <Button className="mt-4" onClick={() => setShowInventoryDialog(true)}>Add First Item</Button>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Staff Tab */}
        <TabsContent value="staff" className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold">Mall Staff ({mallStaff.length})</h3>
              <p className="text-sm text-muted-foreground">Operations, security, and housekeeping personnel</p>
            </div>
            <Button onClick={() => navigate('/users')} data-testid="manage-staff-btn">
              <UserCog className="w-4 h-4 mr-2" />
              Manage Users
            </Button>
          </div>
          
          {/* Staff by Department */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Group staff by role */}
            {['ops_manager', 'technician', 'security_supervisor', 'security_guard', 'housekeeping_supervisor', 'housekeeping_staff'].map(role => {
              const staffInRole = mallStaff.filter(s => s.role === role);
              if (staffInRole.length === 0) return null;
              
              return (
                <Card key={role}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm capitalize">{role.replace(/_/g, ' ')}s ({staffInRole.length})</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {staffInRole.map(staff => (
                        <div key={staff.id} className="flex items-center justify-between p-2 bg-muted/30 rounded">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                              <span className="text-xs font-medium">{staff.name?.charAt(0)}</span>
                            </div>
                            <div>
                              <p className="text-sm font-medium">{staff.name}</p>
                              <p className="text-xs text-muted-foreground">{staff.shift || 'No shift'}</p>
                            </div>
                          </div>
                          <Badge variant={staff.is_active ? 'default' : 'secondary'} className={staff.is_active ? 'bg-green-500/20 text-green-400' : ''}>
                            {staff.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          
          {mallStaff.length === 0 && (
            <Card className="p-12 text-center">
              <UserCog className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground">No staff members assigned to this mall</p>
              <Button className="mt-4" onClick={() => navigate('/users')}>Add Staff</Button>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Asset Dialog */}
      <Dialog open={showAssetDialog} onOpenChange={setShowAssetDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Asset</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Asset Name *</Label>
              <Input value={assetForm.asset_name} onChange={e => setAssetForm({...assetForm, asset_name: e.target.value})} placeholder="e.g., HVAC Unit A1" data-testid="asset-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category *</Label>
                <Select value={assetForm.asset_category} onValueChange={v => setAssetForm({...assetForm, asset_category: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ASSET_CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Criticality</Label>
                <Select value={assetForm.criticality} onValueChange={v => setAssetForm({...assetForm, criticality: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Floor</Label>
                <Input type="number" value={assetForm.floor} onChange={e => setAssetForm({...assetForm, floor: parseInt(e.target.value) || 0})} />
              </div>
              <div>
                <Label>Zone/Location</Label>
                <Input value={assetForm.location_zone} onChange={e => setAssetForm({...assetForm, location_zone: e.target.value})} placeholder="e.g., North Wing" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Manufacturer</Label>
                <Input value={assetForm.manufacturer} onChange={e => setAssetForm({...assetForm, manufacturer: e.target.value})} />
              </div>
              <div>
                <Label>Model Number</Label>
                <Input value={assetForm.model_number} onChange={e => setAssetForm({...assetForm, model_number: e.target.value})} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssetDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateAsset} data-testid="save-asset-btn">Create Asset</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Maintenance Dialog */}
      <Dialog open={showMaintenanceDialog} onOpenChange={setShowMaintenanceDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Maintenance Job</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title *</Label>
              <Input value={maintenanceForm.title} onChange={e => setMaintenanceForm({...maintenanceForm, title: e.target.value})} placeholder="e.g., HVAC not cooling" data-testid="maintenance-title-input" />
            </div>
            <div>
              <Label>Description *</Label>
              <Textarea value={maintenanceForm.description} onChange={e => setMaintenanceForm({...maintenanceForm, description: e.target.value})} placeholder="Describe the issue..." />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Type</Label>
                <Select value={maintenanceForm.maintenance_type} onValueChange={v => setMaintenanceForm({...maintenanceForm, maintenance_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="preventive">Preventive</SelectItem>
                    <SelectItem value="breakdown">Breakdown</SelectItem>
                    <SelectItem value="corrective">Corrective</SelectItem>
                    <SelectItem value="emergency">Emergency</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priority</Label>
                <Select value={maintenanceForm.priority} onValueChange={v => setMaintenanceForm({...maintenanceForm, priority: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Linked Asset</Label>
              <Select value={maintenanceForm.asset_id || "none"} onValueChange={v => setMaintenanceForm({...maintenanceForm, asset_id: v === "none" ? "" : v})}>
                <SelectTrigger><SelectValue placeholder="Select asset (optional)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {assets.map(asset => (
                    <SelectItem key={asset.id} value={asset.id}>{asset.asset_name} ({asset.asset_code})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Floor</Label>
                <Input type="number" value={maintenanceForm.floor} onChange={e => setMaintenanceForm({...maintenanceForm, floor: parseInt(e.target.value) || 0})} />
              </div>
              <div>
                <Label>Zone</Label>
                <Input value={maintenanceForm.location_zone} onChange={e => setMaintenanceForm({...maintenanceForm, location_zone: e.target.value})} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMaintenanceDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateMaintenanceJob} data-testid="save-maintenance-btn">Create Job</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Incident Dialog */}
      <Dialog open={showIncidentDialog} onOpenChange={setShowIncidentDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Report Incident</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title *</Label>
              <Input value={incidentForm.title} onChange={e => setIncidentForm({...incidentForm, title: e.target.value})} placeholder="e.g., Water leak in washroom" data-testid="incident-title-input" />
            </div>
            <div>
              <Label>Description *</Label>
              <Textarea value={incidentForm.description} onChange={e => setIncidentForm({...incidentForm, description: e.target.value})} placeholder="Describe the incident..." />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Type</Label>
                <Select value={incidentForm.incident_type} onValueChange={v => setIncidentForm({...incidentForm, incident_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="theft">Theft</SelectItem>
                    <SelectItem value="fire">Fire</SelectItem>
                    <SelectItem value="medical">Medical</SelectItem>
                    <SelectItem value="accident">Accident</SelectItem>
                    <SelectItem value="vandalism">Vandalism</SelectItem>
                    <SelectItem value="power_failure">Power Failure</SelectItem>
                    <SelectItem value="water_leak">Water Leak</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Severity</Label>
                <Select value={incidentForm.severity} onValueChange={v => setIncidentForm({...incidentForm, severity: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Floor</Label>
                <Input type="number" value={incidentForm.floor} onChange={e => setIncidentForm({...incidentForm, floor: parseInt(e.target.value) || 0})} />
              </div>
              <div>
                <Label>Zone</Label>
                <Input value={incidentForm.location_zone} onChange={e => setIncidentForm({...incidentForm, location_zone: e.target.value})} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowIncidentDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateIncident} data-testid="save-incident-btn">Report Incident</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Work Permit Dialog */}
      <Dialog open={showPermitDialog} onOpenChange={setShowPermitDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Work Permit</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Permit Type *</Label>
                <Select value={permitForm.permit_type} onValueChange={v => setPermitForm({...permitForm, permit_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="electrical">Electrical</SelectItem>
                    <SelectItem value="civil">Civil</SelectItem>
                    <SelectItem value="hot_work">Hot Work</SelectItem>
                    <SelectItem value="height_work">Height Work</SelectItem>
                    <SelectItem value="confined_space">Confined Space</SelectItem>
                    <SelectItem value="general">General</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Workers Count</Label>
                <Input type="number" value={permitForm.workers_count} onChange={e => setPermitForm({...permitForm, workers_count: parseInt(e.target.value) || 1})} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Contractor Name *</Label>
                <Input value={permitForm.contractor_name} onChange={e => setPermitForm({...permitForm, contractor_name: e.target.value})} data-testid="permit-contractor-name-input" />
              </div>
              <div>
                <Label>Company *</Label>
                <Input value={permitForm.contractor_company} onChange={e => setPermitForm({...permitForm, contractor_company: e.target.value})} />
              </div>
            </div>
            <div>
              <Label>Phone *</Label>
              <Input value={permitForm.contractor_phone} onChange={e => setPermitForm({...permitForm, contractor_phone: e.target.value})} />
            </div>
            <div>
              <Label>Work Description *</Label>
              <Textarea value={permitForm.work_description} onChange={e => setPermitForm({...permitForm, work_description: e.target.value})} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Floor</Label>
                <Input type="number" value={permitForm.floor} onChange={e => setPermitForm({...permitForm, floor: parseInt(e.target.value) || 0})} />
              </div>
              <div>
                <Label>Zone</Label>
                <Input value={permitForm.location_zone} onChange={e => setPermitForm({...permitForm, location_zone: e.target.value})} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date *</Label>
                <Input type="date" value={permitForm.start_date} onChange={e => setPermitForm({...permitForm, start_date: e.target.value})} />
              </div>
              <div>
                <Label>End Date *</Label>
                <Input type="date" value={permitForm.end_date} onChange={e => setPermitForm({...permitForm, end_date: e.target.value})} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPermitDialog(false)}>Cancel</Button>
            <Button onClick={handleCreatePermit} data-testid="save-permit-btn">Create Permit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Gate Pass Dialog */}
      <Dialog open={showGatePassDialog} onOpenChange={setShowGatePassDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Gate Pass</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Pass Type *</Label>
              <Select value={gatePassForm.pass_type} onValueChange={v => setGatePassForm({...gatePassForm, pass_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="visitor">Visitor</SelectItem>
                  <SelectItem value="contractor">Contractor</SelectItem>
                  <SelectItem value="material_in">Material In</SelectItem>
                  <SelectItem value="material_out">Material Out</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Person Name *</Label>
              <Input value={gatePassForm.person_name} onChange={e => setGatePassForm({...gatePassForm, person_name: e.target.value})} data-testid="gatepass-person-name-input" />
            </div>
            <div>
              <Label>Company</Label>
              <Input value={gatePassForm.company} onChange={e => setGatePassForm({...gatePassForm, company: e.target.value})} />
            </div>
            <div>
              <Label>Purpose *</Label>
              <Input value={gatePassForm.purpose} onChange={e => setGatePassForm({...gatePassForm, purpose: e.target.value})} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Valid From *</Label>
                <Input type="date" value={gatePassForm.valid_from} onChange={e => setGatePassForm({...gatePassForm, valid_from: e.target.value})} />
              </div>
              <div>
                <Label>Valid Until *</Label>
                <Input type="date" value={gatePassForm.valid_until} onChange={e => setGatePassForm({...gatePassForm, valid_until: e.target.value})} />
              </div>
            </div>
            <div>
              <Label>Vehicle Number</Label>
              <Input value={gatePassForm.vehicle_number} onChange={e => setGatePassForm({...gatePassForm, vehicle_number: e.target.value})} placeholder="Optional" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGatePassDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateGatePass} data-testid="save-gatepass-btn">Create Pass</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Vendor Dialog */}
      <Dialog open={showVendorDialog} onOpenChange={setShowVendorDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Vendor</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Vendor Name *</Label>
              <Input value={vendorForm.vendor_name} onChange={e => setVendorForm({...vendorForm, vendor_name: e.target.value})} data-testid="vendor-name-input" />
            </div>
            <div>
              <Label>Vendor Type *</Label>
              <Select value={vendorForm.vendor_type} onValueChange={v => setVendorForm({...vendorForm, vendor_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="maintenance">Maintenance</SelectItem>
                  <SelectItem value="housekeeping">Housekeeping</SelectItem>
                  <SelectItem value="security">Security</SelectItem>
                  <SelectItem value="amc">AMC Provider</SelectItem>
                  <SelectItem value="supplier">Supplier</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Contact Person *</Label>
              <Input value={vendorForm.contact_person} onChange={e => setVendorForm({...vendorForm, contact_person: e.target.value})} />
            </div>
            <div>
              <Label>Contact Phone *</Label>
              <Input value={vendorForm.contact_phone} onChange={e => setVendorForm({...vendorForm, contact_phone: e.target.value})} />
            </div>
            <div>
              <Label>Contact Email *</Label>
              <Input type="email" value={vendorForm.contact_email} onChange={e => setVendorForm({...vendorForm, contact_email: e.target.value})} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVendorDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateVendor} data-testid="save-vendor-btn">Add Vendor</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule Dialog */}
      <Dialog open={showScheduleDialog} onOpenChange={setShowScheduleDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Cleaning Schedule</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Zone *</Label>
                <Input value={scheduleForm.zone} onChange={e => setScheduleForm({...scheduleForm, zone: e.target.value})} placeholder="e.g., North Wing" data-testid="schedule-zone-input" />
              </div>
              <div>
                <Label>Floor *</Label>
                <Input type="number" value={scheduleForm.floor} onChange={e => setScheduleForm({...scheduleForm, floor: parseInt(e.target.value) || 0})} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Area Type *</Label>
                <Select value={scheduleForm.area_type} onValueChange={v => setScheduleForm({...scheduleForm, area_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="washroom">Washroom</SelectItem>
                    <SelectItem value="common_area">Common Area</SelectItem>
                    <SelectItem value="food_court">Food Court</SelectItem>
                    <SelectItem value="parking">Parking</SelectItem>
                    <SelectItem value="lobby">Lobby</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Frequency *</Label>
                <Select value={scheduleForm.frequency} onValueChange={v => setScheduleForm({...scheduleForm, frequency: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hourly">Hourly</SelectItem>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Start Time</Label>
              <Input type="time" value={scheduleForm.start_time} onChange={e => setScheduleForm({...scheduleForm, start_time: e.target.value})} />
            </div>
            <div>
              <Label>Assigned Staff</Label>
              <Select value={scheduleForm.assigned_staff || "unassigned"} onValueChange={v => setScheduleForm({...scheduleForm, assigned_staff: v === "unassigned" ? "" : v})}>
                <SelectTrigger><SelectValue placeholder="Select staff" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {mallStaff.filter(s => s.role === 'housekeeping_staff' || s.role === 'housekeeping_supervisor').map(staff => (
                    <SelectItem key={staff.id} value={staff.name}>{staff.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={scheduleForm.notes} onChange={e => setScheduleForm({...scheduleForm, notes: e.target.value})} placeholder="Any special instructions..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowScheduleDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateSchedule} data-testid="save-schedule-btn">Create Schedule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Inventory Item Dialog */}
      <Dialog open={showInventoryDialog} onOpenChange={setShowInventoryDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Inventory Item</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Item Name *</Label>
              <Input value={inventoryForm.item_name} onChange={e => setInventoryForm({...inventoryForm, item_name: e.target.value})} placeholder="e.g., Hand Soap Refill" data-testid="inventory-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category *</Label>
                <Select value={inventoryForm.category} onValueChange={v => setInventoryForm({...inventoryForm, category: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cleaning_supplies">Cleaning Supplies</SelectItem>
                    <SelectItem value="spare_parts">Spare Parts</SelectItem>
                    <SelectItem value="office_supplies">Office Supplies</SelectItem>
                    <SelectItem value="safety_equipment">Safety Equipment</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Unit *</Label>
                <Select value={inventoryForm.unit} onValueChange={v => setInventoryForm({...inventoryForm, unit: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pcs">Pieces</SelectItem>
                    <SelectItem value="liters">Liters</SelectItem>
                    <SelectItem value="kg">Kilograms</SelectItem>
                    <SelectItem value="boxes">Boxes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Current Stock</Label>
                <Input type="number" value={inventoryForm.current_stock} onChange={e => setInventoryForm({...inventoryForm, current_stock: parseFloat(e.target.value) || 0})} />
              </div>
              <div>
                <Label>Min Level</Label>
                <Input type="number" value={inventoryForm.min_stock_level} onChange={e => setInventoryForm({...inventoryForm, min_stock_level: parseFloat(e.target.value) || 0})} />
              </div>
              <div>
                <Label>Reorder</Label>
                <Input type="number" value={inventoryForm.reorder_level} onChange={e => setInventoryForm({...inventoryForm, reorder_level: parseFloat(e.target.value) || 0})} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Unit Price (₹)</Label>
                <Input type="number" value={inventoryForm.unit_price} onChange={e => setInventoryForm({...inventoryForm, unit_price: parseFloat(e.target.value) || 0})} />
              </div>
              <div>
                <Label>Location</Label>
                <Input value={inventoryForm.location} onChange={e => setInventoryForm({...inventoryForm, location: e.target.value})} placeholder="e.g., Store Room A" />
              </div>
            </div>
            <div>
              <Label>Supplier</Label>
              <Input value={inventoryForm.supplier} onChange={e => setInventoryForm({...inventoryForm, supplier: e.target.value})} placeholder="Supplier name" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInventoryDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateInventoryItem} data-testid="save-inventory-btn">Add Item</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stock Movement Dialog */}
      <Dialog open={showStockDialog} onOpenChange={setShowStockDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Record Stock Movement</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Item *</Label>
              <Select value={stockForm.item_id} onValueChange={v => setStockForm({...stockForm, item_id: v})}>
                <SelectTrigger><SelectValue placeholder="Select item" /></SelectTrigger>
                <SelectContent>
                  {inventoryItems.map(item => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.item_name} ({item.current_stock} {item.unit})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Movement Type *</Label>
                <Select value={stockForm.movement_type} onValueChange={v => setStockForm({...stockForm, movement_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in">Stock In</SelectItem>
                    <SelectItem value="out">Stock Out</SelectItem>
                    <SelectItem value="adjustment">Adjustment</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Quantity *</Label>
                <Input type="number" value={stockForm.quantity} onChange={e => setStockForm({...stockForm, quantity: parseFloat(e.target.value) || 0})} />
              </div>
            </div>
            <div>
              <Label>Reference</Label>
              <Input value={stockForm.reference} onChange={e => setStockForm({...stockForm, reference: e.target.value})} placeholder="e.g., PO-2024-001" />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={stockForm.notes} onChange={e => setStockForm({...stockForm, notes: e.target.value})} placeholder="Any additional notes..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStockDialog(false)}>Cancel</Button>
            <Button onClick={handleStockMovement} data-testid="save-stock-btn">Record Movement</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
